import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "../../lib/api/client";
import { toast } from "../../lib/ui/toast";
import type { Problem, Problems } from "./model";

/**
 * The fleet sheets' shared plumbing: input classes, a problem → message
 * translator, and the one place a failed save is routed (field or toast),
 * like `applyApiError` for the react-hook-form modals.
 */

export const inputClass = (invalid: boolean, extra = "") => `input input-sm w-full ${invalid ? "input-error" : ""} ${extra}`;

/** Translate a check's problem (`transportFleet:validation.*`). */
export function useProblemText(): (p: Problem | undefined) => string | undefined {
	const { t } = useTranslation("transportFleet");
	// biome-ignore lint/suspicious/noExplicitAny: the key is built from a check code; every code has a key (i18n-check)
	return (p) => (p ? (p.text ?? t(`validation.${p.key}` as any, p.values ?? {})) : undefined);
}

/** The trips a 409 names (`details.refs`), formatted by the API (`TRIP-000123`). */
export function refsOf(err: ApiError): string[] {
	const refs = err.details?.["refs"];
	return Array.isArray(refs) ? refs.filter((r): r is string => typeof r === "string") : [];
}

/**
 * A refused save → a field message when the error names one this sheet
 * renders, else a toast. 422 `field_errors` mark the field "invalid value"
 * (the API speaks English; the sheet's own checks carry the translated copy).
 */
export function routeSaveError<F extends string>(
	err: unknown,
	known: readonly F[],
	setProblems: (p: Problems<F>) => void,
	prefix: string,
	special?: (err: ApiError) => Problems<F> | null,
): void {
	if (err instanceof ApiError) {
		const hit = special?.(err);
		if (hit && Object.keys(hit).length > 0) {
			setProblems(hit);
			return;
		}
		const fields = Object.keys(err.fieldErrors).filter((k): k is F => (known as readonly string[]).includes(k));
		if (fields.length > 0) {
			setProblems(Object.fromEntries(fields.map((f) => [f, { key: "invalidValue" }])) as Problems<F>);
			return;
		}
	}
	toast.error(err, prefix);
}

/** A small uppercase-free heading inside a sheet (her `.esub`). */
export function SheetSubhead({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
	return (
		<div className="flex items-baseline justify-between gap-[0.5rem]">
			<h3 className="text-[0.84375rem] font-semibold text-gms-navy-ink">{children}</h3>
			{aside}
		</div>
	);
}
